import React from 'react';
import { AlertOctagon, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { AlertSeverity } from '../../types/alert';
import { getSeverityVisuals } from '../../utils/formatters';

interface SeverityBadgeProps {
  severity: AlertSeverity;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
}

export const SeverityBadge: React.FC<SeverityBadgeProps> = ({
  severity,
  size = 'md',
  showLabel = true,
}) => {
  const visuals = getSeverityVisuals(severity);

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
  };

  const badgePaddings = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3 py-1.5 text-xs',
  };

  // Modern smart pills with subtle border & background
  const badgeColors: Record<AlertSeverity, string> = {
    Extreme: 'bg-red-600 text-white shadow-2xs font-bold border border-red-500',
    Severe: 'bg-orange-500 text-white shadow-2xs font-bold border border-orange-400',
    Moderate: 'bg-amber-500 text-white shadow-2xs font-bold border border-amber-400',
    Minor: 'bg-blue-600 text-white shadow-2xs font-bold border border-blue-500',
    Unknown: 'bg-slate-600 text-white shadow-2xs font-bold border border-slate-500',
  };

  const renderIcon = () => {
    switch (visuals.iconName) {
      case 'AlertOctagon':
        return <AlertOctagon className={iconSizes[size]} aria-hidden="true" />;
      case 'AlertTriangle':
        return <AlertTriangle className={iconSizes[size]} aria-hidden="true" />;
      case 'AlertCircle':
        return <AlertCircle className={iconSizes[size]} aria-hidden="true" />;
      default:
        return <Info className={iconSizes[size]} aria-hidden="true" />;
    }
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-wider rounded-lg ${badgeColors[severity] || badgeColors.Unknown} ${badgePaddings[size]}`}
      role="status"
      aria-label={`Alert severity level: ${severity}`}
    >
      {renderIcon()}
      {showLabel && <span>{severity}</span>}
    </span>
  );
};
